import { makeAutoObservable, runInAction } from 'mobx';
import { CampaignLinkDto, CreateCampaignLinkDto, LookupKeyEnum, UpdateCampaignLinkDto } from '../../../defs/api';
import type { RootStore } from '../root-store';

export type CampaignLinkDialogMode = 'create' | 'update';

export type CampaignLinkFormDto = CreateCampaignLinkDto | UpdateCampaignLinkDto;

export class AdminCampaignLinkStore {
    rootStore: RootStore;

    campaignLinks: CampaignLinkDto[] = [];

    editedCampaignLink: CampaignLinkDto | null = null;

    dialogMode: CampaignLinkDialogMode = 'create';

    dialogOpened = false;

    loading = false;

    deleteInProgress = false;

    createOrUpdateErrorMessage: string | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    async fetchCampaignLinks(): Promise<void> {
        try {
            this.setLoading(true);
            const campaignLinks =
                await this.rootStore.apiClient.campaignLinksController.campaignLinkControllerGetAdminList();

            runInAction(() => {
                this.campaignLinks = campaignLinks;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.setLoading(false);
        }
    }

    async create(input: CreateCampaignLinkDto): Promise<void> {
        this.createOrUpdateErrorMessage = null;
        try {
            this.setLoading(true);
            await this.rootStore.apiClient.campaignLinksController.campaignLinkControllerCreate({
                createCampaignLinkDto: input,
            });
            await this.fetchCampaignLinks();
            this.closeDialog();
        } catch (e) {
            runInAction(() => {
                this.createOrUpdateErrorMessage = e.message;
            });
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.setLoading(false);
        }
    }

    async update(input: UpdateCampaignLinkDto): Promise<void> {
        if (!this.editedCampaignLink) return;

        this.createOrUpdateErrorMessage = null;
        try {
            this.setLoading(true);
            await this.rootStore.apiClient.campaignLinksController.campaignLinkControllerUpdate({
                id: this.editedCampaignLink.id,
                updateCampaignLinkDto: input,
            });
            await this.fetchCampaignLinks();
            this.closeDialog();
        } catch (e) {
            runInAction(() => {
                this.createOrUpdateErrorMessage = e.message;
            });
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.setLoading(false);
        }
    }

    async delete(id: string): Promise<void> {
        this.setDeleteInProgress(true);
        try {
            const deleted = await this.rootStore.apiClient.campaignLinksController.campaignLinkControllerDelete({
                id,
            });

            if (deleted) {
                await this.fetchCampaignLinks();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.setDeleteInProgress(false);
        }
    }

    openCreateDialog(): void {
        this.dialogMode = 'create';
        this.editedCampaignLink = null;
        this.dialogOpened = true;
        this.rootStore.alertStore.setModalPlacement();
    }

    openUpdateDialog(campaignLink: CampaignLinkDto): void {
        this.dialogMode = 'update';
        this.editedCampaignLink = campaignLink;
        this.dialogOpened = true;
        this.rootStore.alertStore.setModalPlacement();
    }

    closeDialog(): void {
        this.dialogOpened = false;
        this.editedCampaignLink = null;
        this.createOrUpdateErrorMessage = null;
        this.rootStore.alertStore.setDefaultPlacement();
    }

    setLoading(loading: boolean): void {
        this.loading = loading;
    }

    setDeleteInProgress(deleteInProgress: boolean): void {
        this.deleteInProgress = deleteInProgress;
    }

    get hasCampaignLinks(): boolean {
        return this.campaignLinks.length > 0;
    }

    get initialDialogValues(): CampaignLinkFormDto {
        if (this.dialogMode === 'update' && this.editedCampaignLink) {
            return {
                slug: this.editedCampaignLink.slug,
                name: this.editedCampaignLink.name,
                isActive: this.editedCampaignLink.isActive,
                validFrom: this.editedCampaignLink.validFrom,
                validTo: this.editedCampaignLink.validTo,
                affiliateCode: this.editedCampaignLink.affiliateCode,
                trialDays: this.editedCampaignLink.trialDays,
                couponId: this.editedCampaignLink.couponId,
                firstPurchaseOnly: this.editedCampaignLink.firstPurchaseOnly,
                allowPaymentWithoutCreditCard: this.editedCampaignLink.allowPaymentWithoutCreditCard,
                preferredLookupKey: this.editedCampaignLink.preferredLookupKey,
                allowedLookupKeys: this.editedCampaignLink.allowedLookupKeys,
            };
        }

        return {
            slug: '',
            name: '',
            isActive: true,
            validFrom: new Date(),
            validTo: null,
            affiliateCode: null,
            trialDays: null,
            couponId: null,
            firstPurchaseOnly: false,
            allowPaymentWithoutCreditCard: false,
            preferredLookupKey: null,
            allowedLookupKeys: [LookupKeyEnum.PremiumYearlyCzk202411],
        };
    }
}
